import { stepForCvField } from "@/lib/cvBuilderPresentation";
import { useState, useMemo } from "react";
import {
    ShieldCheck,
    ShieldAlert,
    AlertTriangle,
    CheckCircle2,
    Clock,
    Layers,
    FileQuestion,
    Check,
    ArrowUpRight,
    HelpCircle,
    Info,
    RotateCcw,
    XCircle,
    UserCheck,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { isAnomalyActive, isAnomalyTreated } from "@/stores/cvBuilderStore";

const CATEGORY_CONFIG = {
    all: {
        label: "Toutes les alertes",
        icon: ShieldCheck,
        badgeClass: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    },
    unsupported_fact: {
        label: "Information non supportée",
        icon: AlertTriangle,
        badgeClass: "bg-amber-500/10 text-amber-400 border-amber-500/30",
        actionLabel: "Vérifier / Supprimer",
        description: "Compétence ou information absente du CV source",
    },
    unmapped_content: {
        label: "Information non classifiée",
        icon: FileQuestion,
        badgeClass: "bg-purple-500/10 text-purple-400 border-purple-500/30",
        actionLabel: "Affecter",
        description: "Contenu préservé du CV source mais sans cible certaine",
    },
    date_inconsistency: {
        label: "Incohérence de dates",
        icon: Clock,
        badgeClass: "bg-rose-500/10 text-rose-400 border-rose-500/30",
        actionLabel: "Vérifier",
        description: "Dates contradictoires ou périodes ambiguës",
    },
    structural_misassignment: {
        label: "Structure incorrecte",
        icon: Layers,
        badgeClass: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
        actionLabel: "Corriger",
        description: "Projet ou entreprise classifié à tort comme technologie",
    },
    incomplete_sentence: {
        label: "Phrase incomplète",
        icon: HelpCircle,
        badgeClass: "bg-orange-500/10 text-orange-400 border-orange-500/30",
        actionLabel: "Consulter la source",
        description: "Texte potentiellement tronqué dans le CV source",
    },
    missing_significant_content: {
        label: "Information importante manquante",
        icon: ShieldAlert,
        badgeClass: "bg-rose-500/10 text-rose-300 border-rose-500/30",
        actionLabel: "Ajouter",
        description: "Contenu source absent du CV structuré",
    },
};

const mapFieldPathToStep = stepForCvField;

const getFieldScopeLabel = (item) => {
    if (item?.category === "unmapped_content") return "Information à classer";
    const path = String(item?.field_path || "").toLowerCase();
    if (path.startsWith("experiences")) return "Expérience professionnelle";
    if (path.startsWith("skills")) return "Compétences";
    if (path.startsWith("education")) return "Formation";
    if (path.startsWith("languages")) return "Langues";
    if (path.startsWith("certifications")) return "Certifications";
    if (path.startsWith("candidate")) return "Profil du candidat";
    return "CV importé";
};

const isCriticalAnomaly = (item) => {
    if (!item) return false;
    if (item.severity === "critical") return true;
    return ["missing_significant_content", "unsupported_fact", "structural_misassignment"].includes(item.category);
};

export const QualityControlPanel = ({
    isOpen = false,
    onClose = () => {},
    anomalies = [],
    scoringDimensions = null,
    totalScore = 95,
    verificationProgress = null,
    documentQuality = null,
    onResolveAnomaly = () => {},
    onNavigateToStep = () => {},
}) => {
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [filterStatus, setFilterStatus] = useState("active"); // "active" | "critical" | "resolved" | "all"

    // Dismiss justification modal state
    const [dismissTarget, setDismissTarget] = useState(null);
    const [dismissReason, setDismissReason] = useState("Information vérifiée et voulue");

    const safeAnomalies = useMemo(() => (Array.isArray(anomalies) ? anomalies : []), [anomalies]);

    // Grouping & count
    const stats = useMemo(() => {
        const activeList = safeAnomalies.filter((a) => isAnomalyActive(a.status));
        const resolvedList = safeAnomalies.filter((a) => isAnomalyTreated(a.status));
        const criticalActiveList = activeList.filter(isCriticalAnomaly);

        const counts = {
            all: safeAnomalies.length,
            active: activeList.length,
            critical: criticalActiveList.length,
            resolved: resolvedList.length,
            unsupported_fact: safeAnomalies.filter((a) => a.category === "unsupported_fact").length,
            unmapped_content: safeAnomalies.filter((a) => a.category === "unmapped_content").length,
            date_inconsistency: safeAnomalies.filter((a) => a.category === "date_inconsistency").length,
            structural_misassignment: safeAnomalies.filter((a) => a.category === "structural_misassignment").length,
            incomplete_sentence: safeAnomalies.filter((a) => a.category === "incomplete_sentence").length,
            missing_significant_content: safeAnomalies.filter((a) => a.category === "missing_significant_content").length,
        };
        return counts;
    }, [safeAnomalies]);

    const progress = useMemo(() => {
        if (verificationProgress && typeof verificationProgress === "object") {
            return {
                treated: verificationProgress.treated_count ?? verificationProgress.treated ?? stats.resolved,
                total: verificationProgress.total_count ?? verificationProgress.total ?? stats.all,
                percentage: verificationProgress.percentage ?? (stats.all > 0 ? Math.round((stats.resolved / stats.all) * 100) : 100),
            };
        }
        return {
            treated: stats.resolved,
            total: stats.all,
            percentage: stats.all > 0 ? Math.round((stats.resolved / stats.all) * 100) : 100,
        };
    }, [verificationProgress, stats]);

    const filteredAnomalies = useMemo(() => {
        return safeAnomalies.filter((item) => {
            // Category filter
            if (selectedCategory !== "all" && item.category !== selectedCategory) {
                return false;
            }
            // Status filter
            const isActive = isAnomalyActive(item.status);
            const isResolved = isAnomalyTreated(item.status);

            if (filterStatus === "active" && !isActive) return false;
            if (filterStatus === "critical" && (!isActive || !isCriticalAnomaly(item))) return false;
            if (filterStatus === "resolved" && !isResolved) return false;
            return true;
        });
    }, [safeAnomalies, selectedCategory, filterStatus]);

    const handleJumpToField = (item) => {
        const step = mapFieldPathToStep(item.field_path);
        const expMatch = item.field_path ? String(item.field_path).match(/experiences\[(\d+)\]/) : null;
        const missionMatch = item.field_path ? String(item.field_path).match(/missions\[(\d+)\]/) : null;
        const expIndex = expMatch ? parseInt(expMatch[1], 10) : undefined;
        const missionIndex = missionMatch ? parseInt(missionMatch[1], 10) : undefined;

        onClose();
        const navPayload = { step, expIndex, missionIndex, fieldPath: item.field_path, title: [getFieldScopeLabel(item), item.company, item.mission_title].filter(Boolean).join(' — '), sourceText: item.text_raw || '', message: item.reason || item.message || item.description || "Comparez cette information avec le CV source." };
        if (typeof onNavigateToStep === "function") {
            onNavigateToStep(navPayload);
        } else {
            window.dispatchEvent(new CustomEvent("cvbuilder:go-to-step", { detail: navPayload }));
        }
    };

    const handleOpenDismiss = (item) => {
        setDismissTarget(item);
        setDismissReason("Information vérifiée et voulue");
    };

    const handleConfirmDismiss = () => {
        if (!dismissTarget) return;
        onResolveAnomaly(dismissTarget.id, "dismiss", {
            item: dismissTarget,
            justification: dismissReason || "Alerte rejetée par le recruteur",
        });
        setDismissTarget(null);
    };

    const renderAnomalyStatusBadge = (item) => {
        const status = String(item.status || "").toUpperCase();
        if (status === "CONFIRMED" || status === "VERIFIED") {
            return (
                <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40 text-[11px] flex items-center gap-1 font-semibold">
                    <UserCheck className="w-3 h-3" /> Confirmé par recruteur
                </Badge>
            );
        }
        if (status === "CORRECTED" || status === "RESOLVED") {
            return (
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[11px] flex items-center gap-1 font-semibold">
                    <Check className="w-3 h-3" /> Corrigé (USER_EDITED)
                </Badge>
            );
        }
        if (status === "REASSIGNED") {
            return (
                <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/40 text-[11px] flex items-center gap-1 font-semibold">
                    <Layers className="w-3 h-3" /> Réaffecté
                </Badge>
            );
        }
        if (status === "DISMISSED") {
            return (
                <Badge className="bg-slate-700/50 text-slate-300 border-slate-600 text-[11px] flex items-center gap-1">
                    <XCircle className="w-3 h-3" /> Ignoré (justifié)
                </Badge>
            );
        }
        return (
            <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/30 text-[11px]">
                À examiner
            </Badge>
        );
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 bg-slate-950 border-slate-800 text-white shadow-2xl rounded-2xl">
                    {/* Header with two distinct indicators */}
                    <DialogHeader className="p-6 pb-4 border-b border-slate-800 bg-slate-900/60">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-xl bg-blue-600/10 border border-blue-500/30 text-blue-400">
                                    <ShieldCheck className="w-6 h-6" />
                                </div>
                                <div>
                                    <DialogTitle className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
                                        Contrôle qualité & vérification
                                        <Badge className="bg-blue-600/20 text-blue-400 border-blue-500/30 font-medium text-xs">
                                            {stats.active} à traiter
                                        </Badge>
                                    </DialogTitle>
                                    <DialogDescription className="text-xs text-slate-400 mt-0.5">
                                        Traitez les points d'attention sans confondre fidélité documentaire et validation humaine.
                                    </DialogDescription>
                                </div>
                            </div>

                            {/* Dual Metrics Display: Extraction Fidelity vs Recruiter Verification */}
                            <div className="flex items-center gap-2 shrink-0">
                                {/* Metric A: Fidélité documentaire */}
                                <div className="px-3.5 py-2 rounded-xl bg-slate-900 border border-blue-500/30 text-right">
                                    <span className="block text-[10px] uppercase font-bold text-blue-400 tracking-wider">
                                        Fidélité extraction
                                    </span>
                                    <span className="text-base font-extrabold text-white">
                                        {totalScore}%
                                    </span>
                                </div>

                                {/* Metric B: Progression vérification */}
                                <div className="px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-700 text-right">
                                    <span className="block text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                                        Décisions automatiques
                                    </span>
                                    <div className="flex items-center justify-end gap-1.5">
                                        <span className="text-base font-extrabold text-blue-300">
                                            {progress.treated}/{progress.total}
                                        </span>
                                        <span className="text-[10px] text-slate-400">
                                            ({progress.percentage}%)
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {documentQuality?.counts ? (
                            <div className="mt-3 text-xs text-slate-400">
                                {documentQuality.automatically_verified || 0} fait(s) supporté(s), {documentQuality.corrections_applied || 0} correction(s), {documentQuality.anomalies_unresolved || 0} information(s) non confirmée(s), aucune validation individuelle requise.
                            </div>
                        ) : null}

                        {Array.isArray(documentQuality?.corrections) && documentQuality.corrections.length > 0 ? (
                            <details className="mt-3 rounded-xl border border-blue-500/20 bg-slate-950/50 p-3">
                                <summary className="cursor-pointer text-xs font-semibold text-blue-300">
                                    Historique des corrections IA ({documentQuality.corrections.length})
                                </summary>
                                <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1">
                                    {documentQuality.corrections.map((correction) => (
                                        <article key={correction.correction_id} className="rounded-lg border border-slate-800 bg-slate-900/70 p-3 text-xs">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <span className="font-semibold text-slate-200">{correction.field_path}</span>
                                                <span className={correction.status === "AUTO_CORRECTED" ? "text-emerald-300" : "text-amber-300"}>
                                                    {correction.status}
                                                </span>
                                            </div>
                                            <p className="mt-2 text-slate-400">Avant : {JSON.stringify(correction.old_value)}</p>
                                            <p className="mt-1 text-slate-200">Après : {JSON.stringify(correction.new_value)}</p>
                                            <p className="mt-1 text-blue-200">Preuve : {correction.evidence_text || correction.evidence}</p>
                                            <p className="mt-1 text-slate-400">{correction.justification || correction.reason}</p>
                                        </article>
                                    ))}
                                </div>
                            </details>
                        ) : null}

                        {/* Dimensions Breakdown pills */}
                        {scoringDimensions && (
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4 pt-3 border-t border-slate-800/80">
                                {Object.entries(scoringDimensions).map(([key, dim]) => (
                                    <div key={key} className="bg-slate-950/60 border border-slate-800 rounded-lg p-2 text-center">
                                        <span className="block text-[10px] text-slate-400 truncate font-medium">{dim.label}</span>
                                        <span className="text-xs font-bold text-slate-200">{dim.score}%</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </DialogHeader>

                    {/* Sub-navigation & Filters */}
                    <div className="px-6 py-2.5 bg-slate-900/40 border-b border-slate-800/80 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 overflow-x-auto">
                        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
                            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                                const isSelected = selectedCategory === key;
                                const count = key === "all" ? stats.all : (stats[key] || 0);
                                const IconComponent = config.icon;

                                return (
                                    <button
                                        key={key}
                                        onClick={() => setSelectedCategory(key)}
                                        className={`min-h-11 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                                            isSelected
                                                ? "bg-blue-600 text-white shadow-sm"
                                                : "bg-slate-800/50 hover:bg-slate-800 text-slate-300 border border-slate-700/50"
                                        }`}
                                    >
                                        <IconComponent className="w-3.5 h-3.5" aria-hidden="true" />
                                        <span>{config.label}</span>
                                        {count > 0 && (
                                            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                                                isSelected ? "bg-white/20 text-white" : "bg-slate-700 text-slate-300"
                                            }`}>
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Status filter toggle */}
                        <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-lg border border-slate-800 text-xs shrink-0">
                            <button
                                onClick={() => setFilterStatus("active")}
                                className={`min-h-11 px-2.5 py-1 rounded-md font-medium transition-colors ${
                                    filterStatus === "active" ? "bg-blue-600/30 text-blue-300 border border-blue-500/40" : "text-slate-400 hover:text-white"
                                }`}
                            >
                                À traiter ({stats.active})
                            </button>
                            {stats.critical > 0 ? (
                                <button
                                    onClick={() => setFilterStatus("critical")}
                                    className={`min-h-11 px-2.5 py-1 rounded-md font-medium transition-colors ${
                                        filterStatus === "critical" ? "bg-rose-600/30 text-rose-300 border border-rose-500/40" : "text-rose-400 hover:text-rose-200"
                                    }`}
                                >
                                    Critiques — relecture recommandée ({stats.critical})
                                </button>
                            ) : null}
                            <button
                                onClick={() => setFilterStatus("resolved")}
                                className={`min-h-11 px-2.5 py-1 rounded-md font-medium transition-colors ${
                                    filterStatus === "resolved" ? "bg-emerald-600/30 text-emerald-300 border border-emerald-500/40" : "text-slate-400 hover:text-white"
                                }`}
                            >
                                Traités ({stats.resolved})
                            </button>
                            <button
                                onClick={() => setFilterStatus("all")}
                                className={`min-h-11 px-2.5 py-1 rounded-md font-medium transition-colors ${
                                    filterStatus === "all" ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white"
                                }`}
                            >
                                Tous ({stats.all})
                            </button>
                        </div>
                    </div>

                    {/* List of Anomalies */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {filteredAnomalies.length === 0 ? (
                            <div className="py-16 text-center flex flex-col items-center justify-center">
                                <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-3">
                                    <CheckCircle2 className="w-8 h-8" />
                                </div>
                                <h4 className="text-base font-bold text-white">Aucun point dans cette vue</h4>
                                <p className="text-xs text-slate-400 max-w-sm mt-1">
                                    {filterStatus === "active" || filterStatus === "critical"
                                        ? "Tous les points d'attention de cette sélection ont été traités."
                                        : "Aucun élément à afficher pour ce filtre."}
                                </p>
                            </div>
                        ) : (
                            filteredAnomalies.map((item) => {
                                const catConfig = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.all;
                                const isTreated = isAnomalyTreated(item.status);
                                const isCritical = isCriticalAnomaly(item);

                                return (
                                    <div
                                        key={item.id}
                                        className={`p-5 rounded-xl border transition-all ${
                                            isTreated
                                                ? "bg-slate-900/30 border-slate-800/80 opacity-80"
                                                : isCritical
                                                    ? "bg-slate-900/90 border-rose-500/30 shadow-lg"
                                                    : "bg-slate-900/80 border-slate-800 hover:border-slate-700 shadow-md"
                                        }`}
                                    >
                                        {/* Card Header */}
                                        <div className="flex items-start justify-between gap-3 mb-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Badge className={`border text-xs font-semibold ${catConfig.badgeClass}`}>
                                                    {catConfig.label}
                                                </Badge>
                                                {isCritical ? (
                                                    <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40 text-[11px] font-bold">
                                                        Critique — relecture recommandée
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-slate-400 border-slate-700 text-[11px]">
                                                        Recommandé
                                                    </Badge>
                                                )}
                                                {item.source_page && (
                                                    <Badge variant="outline" className="text-slate-400 border-slate-700 text-[11px]">
                                                        Page {item.source_page}
                                                    </Badge>
                                                )}
                                                {renderAnomalyStatusBadge(item)}
                                            </div>

                                            <div className="flex items-center gap-1.5 flex-wrap justify-end max-w-sm">
                                                {item.company && (
                                                    <span className="text-[11px] font-semibold text-blue-300 bg-blue-950/60 px-2 py-0.5 rounded border border-blue-800/60 truncate max-w-[150px]" title={item.company}>
                                                        {item.company}
                                                    </span>
                                                )}
                                                {item.mission_title && (
                                                    <span className="text-[11px] text-slate-300 bg-slate-950 px-2 py-0.5 rounded border border-slate-800/80 truncate max-w-[140px]" title={item.mission_title}>
                                                        {item.mission_title}
                                                    </span>
                                                )}
                                                <span className="text-[11px] text-amber-300/90 bg-slate-950 px-2 py-0.5 rounded border border-slate-800/80">
                                                    {item.field_label || getFieldScopeLabel(item)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Content Comparison Grid */}
                                        <div className="space-y-3 my-3">
                                            {/* Source text (when available) */}
                                            {item.text_raw ? (
                                                <div className="p-3 rounded-lg bg-slate-950/80 border border-slate-800 text-xs">
                                                    <span className="block text-[10px] uppercase font-bold text-slate-400 mb-1">
                                                        Texte exact du CV source :
                                                    </span>
                                                    <p className="text-slate-200 font-serif italic text-xs leading-relaxed whitespace-pre-wrap">
                                                        "{item.text_raw}"
                                                    </p>
                                                </div>
                                            ) : (
                                                /* Transparent notice when NO source text exists */
                                                <div className="p-3 rounded-lg bg-amber-950/20 border border-amber-500/20 text-xs text-amber-200/90 flex items-start gap-2">
                                                    <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                                                    <span>
                                                        Cette information n'a pas été retrouvée dans le document source. Vous pouvez la confirmer en connaissance de cause, la corriger ou la conserver comme information ajoutée manuellement (USER_EDITED).
                                                    </span>
                                                </div>
                                            )}

                                            {/* Current value vs Reason */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                                <div className="p-3 rounded-lg bg-slate-950/50 border border-slate-800">
                                                    <span className="block text-[10px] uppercase font-bold text-slate-400 mb-1">
                                                        Valeur actuelle dans le JSON :
                                                    </span>
                                                    <p className="text-slate-300 text-xs break-words">
                                                        {item.current_value || "—"}
                                                    </p>
                                                </div>
                                                <div className="p-3 rounded-lg bg-slate-950/50 border border-slate-800">
                                                    <span className="block text-[10px] uppercase font-bold text-amber-400/90 mb-1">
                                                        Cause de détection :
                                                    </span>
                                                    <p className="text-slate-300 text-xs leading-normal">
                                                        {item.reason}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Suggested Fix */}
                                            {item.suggested_fix && (
                                                <div className="p-3 rounded-lg bg-blue-950/30 border border-blue-500/20 text-xs flex items-start gap-2">
                                                    <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                                                    <div>
                                                        <span className="font-semibold text-blue-200 block text-[11px]">
                                                            Action recommandée :
                                                        </span>
                                                        <span className="text-blue-300/90 text-xs">
                                                            {item.suggested_fix}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Justification note if dismissed */}
                                            {item.justification && (
                                                <div className="p-2.5 rounded-lg bg-slate-800/40 border border-slate-700/60 text-xs text-slate-300">
                                                    <span className="font-bold text-[10px] uppercase text-slate-400 block mb-0.5">
                                                        Motif de rejet enregistré :
                                                    </span>
                                                    <span>{item.justification}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Action Bar */}
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-3 border-t border-slate-800/80 mt-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {!isTreated ? (
                                                    <>
                                                        {/* 1. Confirmer */}
                                                        <Button
                                                            size="sm"
                                                            onClick={() => onResolveAnomaly(item.id, "confirm", item)}
                                                            className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold min-h-11 px-3.5 rounded-lg flex items-center gap-1.5 shadow-sm"
                                                        >
                                                            <UserCheck className="w-3.5 h-3.5" />
                                                            Confirmer
                                                        </Button>

                                                        {/* 2. Corriger (si autofix disponible) */}
                                                        {item.auto_fix_available === true ? (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => onResolveAnomaly(item.id, "correct", item)}
                                                                className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold min-h-11 px-3.5 rounded-lg flex items-center gap-1.5 shadow-sm"
                                                            >
                                                                <Check className="w-3.5 h-3.5" />
                                                                Appliquer la correction
                                                            </Button>
                                                        ) : null}

                                                        {/* 3. Réaffecter */}
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handleJumpToField(item)}
                                                            className="bg-slate-800/80 hover:bg-slate-800 border-slate-700 text-slate-300 hover:text-white text-xs min-h-11 px-3 rounded-lg"
                                                        >
                                                            <Layers className="w-3.5 h-3.5 mr-1" />
                                                            Réaffecter
                                                        </Button>

                                                        {/* 4. Ignorer avec justification */}
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => handleOpenDismiss(item)}
                                                            className="text-xs text-slate-400 hover:text-rose-300 hover:bg-rose-950/20 min-h-11 px-2.5 flex items-center gap-1"
                                                        >
                                                            <XCircle className="w-3.5 h-3.5" />
                                                            Ignorer
                                                        </Button>
                                                    </>
                                                ) : (
                                                    /* Rouvrir */
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => onResolveAnomaly(item.id, "reopen", item)}
                                                        className="text-xs text-slate-400 hover:text-white min-h-11 px-2.5 flex items-center gap-1"
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5" />
                                                        Rouvrir l'alerte
                                                    </Button>
                                                )}
                                            </div>

                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleJumpToField(item)}
                                                className="text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-950/40 min-h-11 px-2.5 flex items-center gap-1 self-end sm:self-auto"
                                            >
                                                <span>Accéder au champ</span>
                                                <ArrowUpRight className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 px-6 border-t border-slate-800 bg-slate-900/60 flex items-center justify-between">
                        <span className="text-xs text-slate-400">
                            {stats.active > 0
                                ? `${stats.active} point(s) à examiner (${stats.critical} critique(s))`
                                : "Tous les points ont été traités par le recruteur."}
                        </span>
                        <Button
                            onClick={onClose}
                            className="bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-5 min-h-11 rounded-xl border border-slate-700"
                        >
                            Fermer
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal de justification d'exclusion / rejet d'alerte */}
            <Dialog open={Boolean(dismissTarget)} onOpenChange={(open) => !open && setDismissTarget(null)}>
                <DialogContent className="sm:max-w-md bg-slate-900 border border-slate-700 text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base font-bold">
                            <XCircle className="w-5 h-5 text-amber-400" />
                            Ignorer l'alerte avec justification
                        </DialogTitle>
                        <DialogDescription className="text-xs text-slate-400 pt-1">
                            L'alerte sera marquée comme rejetée (DISMISSED) dans l'historique de vérification sans modifier artificiellement le score technique d'extraction.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 py-3 text-xs">
                        <label className="block text-slate-300 font-semibold">
                            Motif du recruteur :
                        </label>
                        <select
                            value={dismissReason}
                            onChange={(e) => setDismissReason(e.target.value)}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-blue-500"
                        >
                            <option value="Information vérifiée et voulue">Information vérifiée et voulue</option>
                            <option value="Faux positif d'extraction">Faux positif d'extraction</option>
                            <option value="Ajout volontaire non présent dans le CV source">Ajout volontaire non présent dans le CV source</option>
                            <option value="Non pertinent pour le profil final">Non pertinent pour le profil final</option>
                            <option value="Autre motif">Autre motif</option>
                        </select>
                        <p className="text-[11px] text-slate-500">
                            Cette décision sera tracée dans le rapport de contrôle qualité.
                        </p>
                    </div>

                    <DialogFooter className="flex sm:justify-between gap-2">
                        <Button
                            variant="ghost"
                            onClick={() => setDismissTarget(null)}
                            className="text-xs text-slate-400 hover:text-white"
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={handleConfirmDismiss}
                            className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold px-4 rounded-lg"
                        >
                            Valider le rejet
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default QualityControlPanel;
